import type { QueryClient } from "@tanstack/react-query";

import type { PageDescriptor } from "@pm2/api";
import { getElementContent, getPageContent } from "@pm2/api";
import { parseElementXML, parsePageXML } from "@pm2/content-engine";
import type { PM2SitemapStore } from "@pm2/sitemap-utils";
import {
  recursivelyFindResourceById,
  getResourcesForPage,
} from "@pm2/sitemap-utils";
import { type PM2ConfigStore } from "@pm2/store";
import { NotFound } from "@pm2/ui-components";
import { BottomNavigation } from "@pm2/ui-layout";
import type { UserIdentity } from "@provider/user-identity";

import { Helmet } from "../../server/utils/module.react-helmet-async";
import { joinDocumentTitle } from "../utils";

import { ContentPage } from "./page.content";
import { ElementPage } from "./page.element";

export async function renderResourcePage(
  queryClient: QueryClient,
  configStore: PM2ConfigStore,
  sitemapStore: PM2SitemapStore,
  user: UserIdentity | null | undefined,
  resourceId: string,
  pageDescriptor: PageDescriptor,
): Promise<JSX.Element> {
  // fetch all ressources related to the currently visited page, so we can
  // determine which one we need to render
  const resources = await getResourcesForPage(
    configStore,
    sitemapStore,
    pageDescriptor,
  );
  const resource = recursivelyFindResourceById(resources, resourceId);

  if (!resource?.src) {
    // give up if the resource isn't actually pointing anywhere
    return <NotFound />;
  }

  // ... otherwise determine whether to render a page or a content element
  let content: JSX.Element;

  const configState = configStore.getState();

  switch (resource.kind) {
    case "ContentElement": {
      const xml = await getElementContent(configState.config.id, resource.src, {
        previewMode: configState.previewMode,
      });
      const ast = xml
        ? await parseElementXML(queryClient, xml, user)
        : undefined;

      content = <ElementPage ast={ast} />;
      break;
    }

    case "Page": {
      const xml = await getPageContent(configState.config.id, resource.src, {
        previewMode: configState.previewMode,
      });
      const ast = xml ? await parsePageXML(queryClient, xml, user) : undefined;

      content = <ContentPage ast={ast} />;
      break;
    }

    default:
      // for all other cases throw a 404 - user was never meant to end up
      // here, the resource should've been handled differently
      return <NotFound />;
  }

  return (
    <>
      <Helmet>
        <title>
          {joinDocumentTitle(
            resource.title,
            pageDescriptor.title,
            configState.config.title,
          )}
        </title>
      </Helmet>
      {content}

      {pageDescriptor && <BottomNavigation pageDescriptor={pageDescriptor} />}
    </>
  );
}
